import { Link } from 'react-router-dom';
import { cx } from '../cx';
import { Reveal } from '../components/Reveal';
import { ChaosField } from '../components/ChaosField';
import { Plate } from '../components/Photo';
import { ArrowRight } from '../components/icons';
import ui from '../ui.module.css';
import s from './ManifestoPage.module.css';

export function ManifestoPage() {
  return (
    <div className={s.page}>
      <header className={s.masthead}>
        <div className={ui.narrow}>
          <span className={ui.crop} aria-hidden="true" />
          <p className={ui.eyebrow}>The manifesto</p>
          <h1 className={s.title}>
            <span className={s.titleSmall}>The mirror should not have an</span>
            <ChaosField text="opinion" italic className={s.titleChaos} />
            <span className={s.titleTail}>about you.</span>
          </h1>
          <p className={s.standfirst}>
            Why we built a try-on product that refuses to talk about your body.
          </p>
          <dl className={s.byline}>
            <div>
              <dt>Publisher</dt>
              <dd>RackSavant</dd>
            </div>
            <div>
              <dt>Document</dt>
              <dd>Founding</dd>
            </div>
            <div>
              <dt>Revised</dt>
              <dd>August 2026</dd>
            </div>
          </dl>
        </div>
      </header>

      <Plate id="mirror" className={s.opener} ratio={21 / 9} focus="50% 32%" />

      <article className={s.essay}>
        <div className={ui.narrow}>
          <Reveal>
            <p className={s.opening}>
              Every morning, a few billion people conduct a small negotiation with the outside
              world. It happens in front of a wardrobe and it takes somewhere between forty seconds
              and forty minutes. The question is never really what to wear. The question is who you
              are willing to be seen as today, in front of which people, and how much of that you
              are prepared to defend.
            </p>

            <p>
              Clothes are the oldest interface we have. Before you speak, before anyone knows your
              name or your job or whether you are having a bad week, the fabric on your shoulders
              has already made a series of claims on your behalf. Serious. Unserious. Rich, or
              pretending. Available. Busy. Dangerous. Recently loved. Most of those claims are
              wrong, and all of them arrive before you get a say. Getting dressed is how you try to
              take some of that back.
            </p>

            <p>
              That is not vanity. Vanity is a word people use to make an ordinary human activity
              sound embarrassing. What is actually happening is closer to diplomacy.
            </p>
          </Reveal>

          <Reveal>
            <h2 className={s.subhead}>Then the audience got much larger.</h2>

            <p>
              For almost all of human history that negotiation had a small audience — a street, an
              office, a party, a person you were trying to impress. It happened in a room and then
              it was over. Photographs were events. You took one on a birthday and saw it three
              weeks later.
            </p>

            <p>
              Now getting dressed is a performance with a permanent, silent, scrollable audience.
              You do not just wear the outfit. You see the outfit, from the outside, in a rectangle,
              in a grid, next to two hundred other rectangles, sometimes for years afterwards. And
              you are aware of that at the moment of choosing. Everyone under about thirty-five has
              learned to dress for the version of themselves that will be looked at later.
            </p>
          </Reveal>

          <Reveal>
            <blockquote className={s.pull}>
              <p>Getting dressed used to be a decision. Somewhere along the way it became content.</p>
            </blockquote>

            <p>
              This is not a complaint about the internet. Being looked at can be enormous fun, and a
              lot of people have built real lives out of it. But it does raise the stakes of a
              wardrobe, and it means the tools people reach for while they are deciding have far
              more influence than a piece of software really deserves.
            </p>
          </Reveal>

          <Reveal>
            <h2 className={s.subhead}>What the industry built for that moment.</h2>

            <p>
              An entire category of fashion technology grew up around this. Virtual try-on, size
              recommendation, fit prediction, body scanning, the slider that asks you to describe
              your shape before you are allowed to see the dress. On paper it is all in service of
              helping you choose well.
            </p>

            <p>
              In practice almost every one of those products is optimised against one number, and
              that number is conversion. Not satisfaction, not confidence, not whether you liked
              what you saw. Whether you bought. Once that is the metric, the design follows
              logically, and the logic runs somewhere unkind. Ask for measurements, because
              measurements reduce returns. Show a body most like theirs, because similarity closes.
              Quietly smooth, lengthen, correct — because the version that sells is not the version
              that is true.
            </p>

            <p>
              None of this is done maliciously. It is done by reasonable people hitting quarterly
              targets. But the effect on the person at the other end is real, and it compounds.
            </p>
          </Reveal>

          <Reveal>
            <blockquote className={cx(s.pull, s.pullWide)}>
              <p>A tool that measures you will eventually teach you to measure yourself.</p>
            </blockquote>

            <p>
              Spend a few years being told, gently and constantly, by software, that your body is a
              set of dimensions to be solved for, and you learn something. Not consciously. You
              simply start looking at yourself the way the software does — as a problem with a fit
              score. We have all met the result of that education. Some of us are the result of it.
            </p>
          </Reveal>
        </div>

        <Plate id="guy" className={s.midPlate} ratio={21 / 9} focus="50% 28%" />

        <div className={ui.narrow}>
          <Reveal>
            <h2 className={s.subhead}>So we wrote two rules first.</h2>

            <p>
              RackSavant started as an argument about what a try-on product should be allowed to do.
              Before any of the interesting parts existed — before moods, before rooms, before any
              of it — there were two lines, and everything else had to fit around them.
            </p>

            <ol className={s.rules}>
              <li>
                Never alter the body. Face, pose, skin, age, shape: untouched. Only the clothes
                change.
              </li>
              <li>
                Never appraise the body. Every sentence the product writes is about the garment.
                Nothing it says is about the person wearing it.
              </li>
            </ol>

            <p>
              These are not values on a careers page. They are instructions sent to the model on
              every single request, and they are the reason some obvious features do not exist here.
              There is no size recommendation. No measurement field. No flattering, no slimming, no
              before-and-after that implies the after is an improvement on you rather than on the
              outfit. We do not train anything on your photographs, and when you delete one, every
              look made from it goes with it.
            </p>

            <p>
              You lose a few percent of conversion that way. We have decided we can live without it.
            </p>
          </Reveal>

          <Reveal>
            <h2 className={s.subhead}>What is left when you take all that out.</h2>

            <p>
              Something better, as it turns out. Once you stop optimising for the purchase, you can
              build for the thing people were actually doing all along, which is working out how
              they want to feel.
            </p>

            <p>
              So the rack is indexed by mood instead of size. You choose <strong>soft</strong>, or{' '}
              <strong>sharp</strong>, or <strong>feral</strong>, or <strong>regal</strong> — the way
              anybody has ever thought about it while standing in front of a wardrobe at eleven at
              night. A finished look comes back with a read rather than a rating: a verdict, an
              energy, the adjectives it is genuinely giving, a colour story, a caption. And the
              reactions are not hearts. They are seven things a friend might actually say, including
              being called a menace, which is information a heart has never once conveyed.
            </p>

            <p>
              The other thing we admitted is that none of this was ever solitary. You already send
              the mirror photo to three people and stand there holding the phone. So there are
              fitting rooms: live, twelve hours, a six-letter code, everybody in there watching the
              looks land as they develop. It is the group chat, in the room, before the decision
              instead of after it.
            </p>
          </Reveal>

          <Reveal>
            <h2 className={s.subhead}>What we are actually for.</h2>

            <p>
              We are not trying to help you buy more clothes. We are not trying to make you look
              like somebody else, and we are extremely not trying to help you look like a smoother
              version of yourself. There is enough software doing that already, and it has not made
              anybody happier.
            </p>

            <p>
              What we want to build is a mirror you go to on purpose. One that remembers what you
              have tried. One that has friends in it. One that will tell you the coat is doing the
              talking, and never, under any circumstances, tell you anything about the person inside
              the coat.
            </p>

            <p>
              You are not the thing being fixed here. You are the reason any of it is interesting.
            </p>
          </Reveal>

          <Reveal>
            <div className={s.signoff}>
              <p className={s.signoffLine}>Try on the feeling.</p>
              <p className={s.signoffBody}>
                That is the whole product, and the whole argument. If any of it stops being true,
                the file you are reading is wrong and we will fix it.
              </p>
              <div className={s.signoffCtas}>
                <Link to="/auth" className={cx(ui.btn, ui.primary)}>
                  Open the studio
                  <ArrowRight size={16} />
                </Link>
                <Link to="/privacy" className={cx(ui.btn, ui.ghost)}>
                  Read the privacy policy
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </article>
    </div>
  );
}
